import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Calendar, ArrowRight, Clock, Users, FileText, Sparkles, CheckCircle, Calculator } from 'lucide-react';

export default function Home() {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section - Gradient Modern */}
      <section className="min-h-[85vh] flex items-center relative overflow-hidden">
        {/* Gradient Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#9B1B30] via-[#6B1D5C] to-[#2E2A88]"></div>
        
        {/* Decorative Elements */}
        <div className="absolute top-20 left-10 w-72 h-72 bg-white/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-white/5 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] border border-white/10 rounded-full"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] border border-white/5 rounded-full"></div>
        
        <div className="max-w-7xl mx-auto px-6 relative z-10 w-full pt-20 md:pt-24">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-full px-5 py-2 mb-8">
              <Sparkles className="w-4 h-4 text-yellow-300" />
              <span className="text-sm text-white font-medium">Neu: Online Terminbuchung</span>
            </div>
            
            <h1 className="text-5xl md:text-7xl font-black text-white mb-6 leading-tight">
              Termine buchen
              <br />
              <span className="bg-gradient-to-r from-yellow-200 via-pink-200 to-cyan-200 bg-clip-text text-transparent">
                war nie einfacher
              </span>
            </h1>
            
            <p className="text-xl text-white/80 mb-10 max-w-2xl mx-auto leading-relaxed">
              Buchen Sie Ihren Beratungstermin in Sekunden. 
              24/7 verfügbar, sofortige Bestätigung.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/termin">
                <Button className="bg-white text-[#9B1B30] hover:bg-gray-100 text-lg px-10 py-7 rounded-2xl font-bold shadow-2xl shadow-black/20">
                  <Calendar className="w-6 h-6 mr-2" />
                  Termin buchen
                </Button>
              </Link>
              <Link to="/login">
                <Button variant="outline" className="text-lg px-10 py-7 rounded-2xl border-2 border-white/30 text-white hover:bg-white/10 font-bold backdrop-blur-sm">
                  Kundenportal
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>

            {/* Sofort-Kostenschätzung (statische Standalone-Seite in /public) */}
            <div className="mt-4 flex justify-center">
              <a
                href="/kostenschaetzung.html"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="cta-kostenschaetzung"
                className="relative inline-block"
              >
                {/* "NEU"-Badge oben rechts an der Umrandung */}
                <span
                  className="absolute -top-2 -right-2 z-10 rounded-full bg-yellow-300 text-[#5d1520] text-[10px] font-black px-2 py-0.5 shadow-lg border border-white/40 tracking-wide select-none animate-pulse"
                  data-testid="cta-kostenschaetzung-new-badge"
                >
                  NEU
                </span>
                <Button
                  variant="ghost"
                  className="text-base px-8 py-6 rounded-2xl bg-white/10 border-2 border-yellow-300/80 text-white hover:bg-white/20 font-semibold backdrop-blur-sm shadow-[0_0_0_1px_rgba(253,224,71,0.35)]"
                >
                  <Calculator className="w-5 h-5 mr-2" />
                  Sofort-Kostenschätzung
                  <ArrowRight className="w-4 h-4 ml-2 opacity-80" />
                </Button>
              </a>
            </div>
            
            <div className="mt-12 flex items-center justify-center gap-8 text-white/60 text-sm">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span>Kostenlos</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span>Schnell</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span>Einfach</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">So funktioniert's</h2>
            <p className="text-gray-500 text-lg">In 3 einfachen Schritten zum Termin</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { num: '01', icon: Calendar, title: 'Datum wählen', desc: 'Wählen Sie Ihr Wunschdatum aus dem Kalender' },
              { num: '02', icon: Clock, title: 'Zeit auswählen', desc: 'Entscheiden Sie sich für einen freien Zeitslot' },
              { num: '03', icon: CheckCircle, title: 'Bestätigen', desc: 'Fertig! Sie erhalten eine Bestätigung' },
            ].map((f, i) => (
              <div key={i} className="relative">
                <div className="bg-white p-8 rounded-3xl shadow-lg hover:shadow-xl transition-shadow border border-gray-100">
                  <span className="text-6xl font-black text-gray-100 absolute top-4 right-6">{f.num}</span>
                  <div className="w-16 h-16 bg-gradient-to-br from-[#9B1B30] to-[#2E2A88] rounded-2xl flex items-center justify-center mb-6">
                    <f.icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{f.title}</h3>
                  <p className="text-gray-500">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-[#9B1B30] to-[#2E2A88]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold text-white mb-4">Bereit loszulegen?</h2>
          <p className="text-white/80 mb-8 text-lg">Buchen Sie jetzt Ihren ersten Termin</p>
          <Link to="/termin">
            <Button className="bg-white text-[#9B1B30] hover:bg-gray-100 px-10 py-6 text-lg font-bold rounded-2xl shadow-2xl">
              Jetzt Termin buchen
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
